//TODO 
// 1. ao cadastrar novo estudante, permanecer na mesma página
//2. 
// src/pages/ManageStudentsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  getAllClassrooms
} from '../services/classroomService.js';
import {
  getAllStudents,
  addStudent,
  deleteStudent
} from '../services/studentService.js';
import { useAuth } from '../components/AuthProvider.js';
import '../styles/ManageStudentsPage.css';

/* ─────────────────────────────────────────────────────────── */

function ManageStudentsPage() {
  const { currentUser } = useAuth();               // { uid, role, … }

  /* ------------ STATE ------------ */
  const [managedClassrooms, setManagedClassrooms] = useState([]);
  const [students, setStudents]                 = useState([]);

  const [currentStudent, setCurrentStudent]     = useState({
    id: null,
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    enrollmentNumber: '',
    classroomId: ''
  });

  const [isLoading, setIsLoading]               = useState(false); // both save & fetch
  const [error, setError]                       = useState('');
  const [filterClassroomId, setFilterId]        = useState('');

  const isTeacherOrAdmin =
    currentUser && ['teacher', 'admin'].includes(currentUser.role);

  /* ------------ HELPERS ------------ */
  const getClassroomName = (id) =>
    managedClassrooms.find((c) => c.id === id)?.name || 'N/A';

  /* ------------ FETCH CLASSROOMS (always filtered by UID) ------------ */
  const fetchManagedClassrooms = useCallback(async () => {
    if (!currentUser?.uid) return;

    setIsLoading(true);
    setError('');

    try {
      const classes = await getAllClassrooms(currentUser.uid); // <‑ always pass UID
      setManagedClassrooms(classes);

      /* sanitize selected filters after reload */
      if (
        filterClassroomId &&
        !classes.some((c) => c.id === filterClassroomId)
      ) {
        setFilterId('');
      }
      if (
        currentStudent.classroomId &&
        !classes.some((c) => c.id === currentStudent.classroomId)
      ) {
        setCurrentStudent((prev) => ({ ...prev, classroomId: '' }));
      }
    } catch (err) {
      console.error('Fetch Managed Classrooms Error:', err);
      setError('Failed to fetch your classrooms. Please try again.');
      setManagedClassrooms([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, filterClassroomId, currentStudent.classroomId]);

  /* ------------ FETCH STUDENTS ------------ */
  const fetchStudents = useCallback(async () => {
    if (managedClassrooms.length === 0 && !filterClassroomId) {
      setStudents([]);
      return;
    }

    setIsLoading(true);
    setError((prev) =>
      prev.startsWith('Failed to fetch your classrooms') ? prev : ''
    );

    try {
      let fetched;
      if (filterClassroomId) {
        fetched = await getAllStudents(filterClassroomId);
      } else {
        /* get all and filter locally by managed classrooms */
        const all = await getAllStudents();
        const ids = managedClassrooms.map((c) => c.id);
        fetched = all.filter((stu) => ids.includes(stu.classroomId));
      }

      fetched.sort((a, b) => a.name.localeCompare(b.name));
      setStudents(fetched);
    } catch (err) {
      console.error('Fetch Students Error:', err);
      setError('Failed to fetch students. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [managedClassrooms, filterClassroomId]);

  /* ------------ INITIAL LOAD / ROLE CHANGE ------------ */
  useEffect(() => {
    if (isTeacherOrAdmin) {
      fetchManagedClassrooms();
    } else if (!currentUser) {
      setError('Please log in.');
    } else {
      setError('Access denied.');
    }
  }, [currentUser, isTeacherOrAdmin, fetchManagedClassrooms]);

  /* ------------ RELOAD STUDENTS WHEN CLASSROOMS / FILTER CHANGE ------------ */
  useEffect(() => {
    if (managedClassrooms.length > 0 || filterClassroomId) {
      fetchStudents();
    } else {
      setStudents([]);
    }
  }, [managedClassrooms, filterClassroomId, fetchStudents]);

  /* ------------ HANDLERS ------------ */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentStudent((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterChange = (e) => setFilterId(e.target.value);

  const resetForm = () =>
    setCurrentStudent({
      id: null,
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      enrollmentNumber: '',
      classroomId: ''
    });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (currentStudent.password !== currentStudent.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!currentStudent.classroomId) {
      setError('Please select a classroom.');
      return;
    }
    if (
      !managedClassrooms.some((c) => c.id === currentStudent.classroomId)
    ) {
      setError('You can only add students to your classrooms.');
      return;
    }

    setIsLoading(true);
    setError('');

    const { confirmPassword, id, ...payload } = currentStudent;

    try {
      await addStudent(payload);
      resetForm();
      await fetchStudents();
    } catch (err) {
      console.error('Add Student Error:', err);
      setError(err.message || 'Failed to add student.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (student) => {
    if (
      !student.classroomId ||
      !managedClassrooms.some((c) => c.id === student.classroomId)
    ) {
      setError('You do not have permission to delete this student.');
      return;
    }

    if (
      window.confirm(
        `Delete student "${student.name}"? This action cannot be undone.`
      )
    ) {
      setIsLoading(true);
      setError('');
      try {
        await deleteStudent(student.id);
        await fetchStudents();
      } catch (err) {
        console.error('Delete Student Error:', err);
        setError(err.message || 'Failed to delete student.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  /* ------------ GUARD ------------ */
  if (!isTeacherOrAdmin) {
    return (
      <div className="manage-students-container">
        <h2>Manage Students</h2>
        <p className="error-message">{error || 'Access Denied.'}</p>
      </div>
    );
  }

  /* ------------ RENDER ------------ */
  return (
    <div className="manage-students-container">
      <h2>Manage Students</h2>

      {error &&
        !error.startsWith('Failed to fetch your classrooms') && (
          <p className="error-message">{error}</p>
        )}

      {/* --- Add student form --- */}
      <form onSubmit={handleSubmit} className="student-form">
        <h3>Add New Student</h3>

        <input
          type="text"
          name="name"
          placeholder="Full Name"
          value={currentStudent.name}
          onChange={handleInputChange}
          required
        />

        <input
          type="email"
          name="email"
          placeholder="Email Address"
          value={currentStudent.email}
          onChange={handleInputChange}
          required
        />

        <input
          type="text"
          name="enrollmentNumber"
          placeholder="Enrollment Number"
          value={currentStudent.enrollmentNumber}
          onChange={handleInputChange}
          required
        />

        <input
          type="password"
          name="password"
          placeholder="Password"
          value={currentStudent.password}
          onChange={handleInputChange}
          required
        />

        <input
          type="password"
          name="confirmPassword"
          placeholder="Confirm Password"
          value={currentStudent.confirmPassword}
          onChange={handleInputChange}
          required
        />

        <select
          name="classroomId"
          value={currentStudent.classroomId}
          onChange={handleInputChange}
          required
        >
          <option value="" disabled>
            Select Classroom
          </option>
          {managedClassrooms.map((cls) => (
            <option key={cls.id} value={cls.id}>
              {cls.name}
            </option>
          ))}
        </select>

        <button
          type="submit"
          disabled={isLoading || managedClassrooms.length === 0}
        >
          {isLoading ? 'Saving…' : 'Add Student'}
        </button>

        {managedClassrooms.length === 0 && (
          <p>You must manage at least one classroom to add students.</p>
        )}
      </form>

      {/* --- Student list & filter --- */}
      <div className="student-list-section">
        <h3>Existing Students</h3>

        <div className="student-filter">
          <label htmlFor="filterClassroom">Filter by Classroom:</label>
          <select
            id="filterClassroom"
            value={filterClassroomId}
            onChange={handleFilterChange}
            disabled={managedClassrooms.length === 0}
          >
            <option value="">All Your Classrooms</option>
            {managedClassrooms.map((cls) => (
              <option key={cls.id} value={cls.id}>
                {cls.name}
              </option>
            ))}
          </select>
        </div>

        <div className="student-list">
          {isLoading && <p>Loading students…</p>}

          {!isLoading &&
            students.length === 0 &&
            managedClassrooms.length > 0 && (
              <p>
                No students found in{' '}
                {filterClassroomId
                  ? `"${getClassroomName(filterClassroomId)}"`
                  : 'your managed classrooms'}
                .
              </p>
            )}

          {!isLoading && managedClassrooms.length === 0 && (
            <p>You are not managing any classrooms.</p>
          )}

          <ul>
            {students.map((student) => (
              <li key={student.id}>
                <div>
                  <strong>{student.name}</strong>{' '}
                  ({student.enrollmentNumber})
                  <p>{student.email}</p>
                  <small>
                    Classroom: {getClassroomName(student.classroomId)}
                  </small>
                </div>
                <div>
                  <button
                    onClick={() => handleDelete(student)}
                    disabled={isLoading}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default ManageStudentsPage;
